import { useState } from "react";

type Segment = {
  from: string;
  to: string;
  departsAt: string;
  arrivesAt: string;
  durationMinutes: number;
  airline: string;
  flightNumber: string;
  aircraft?: string;
};

type Itinerary = {
  segments: Segment[];
  totalDurationMinutes: number;
  stops: number;
  layoverMinutesTotal: number;
};

type OfferLike = {
  id: string;
  providerName: string;
  tripType?: "roundtrip" | "oneway";
  price: { amount: number; currency: string };
  outbound: Itinerary;
  return?: Itinerary;
  validatingAirline?: string;
  score?: number;
  admissionFlags?: string[];
  metadata?: {
    serpapi?: {
      returnDate?: string;
      returnDetailsAvailable?: boolean;
    };
  };
};

export function OfferCard({ offer }: { offer: unknown }) {
  const o = offer as OfferLike;
  // Trust the explicit tripType when the provider sent it; fall back to the
  // presence of a return itinerary for older data.
  const roundTrip = o.tripType ? o.tripType === "roundtrip" : Boolean(o.return);
  const [expanded, setExpanded] = useState(false);

  const firstDepart = o.outbound.segments[0]?.from ?? "???";
  const lastArrival = o.outbound.segments[o.outbound.segments.length - 1]?.to ?? "???";
  const departDate = o.outbound.segments[0]?.departsAt.slice(0, 10) ?? "";
  const returnDate =
    o.return?.segments[0]?.departsAt.slice(0, 10) ?? o.metadata?.serpapi?.returnDate;

  return (
    <div className="rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        className="flex w-full items-center justify-between p-4 text-left hover:bg-slate-50 dark:hover:bg-slate-800/50"
        aria-expanded={expanded}
      >
        <div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold text-slate-900 dark:text-slate-100">
              {o.price.currency} {o.price.amount.toLocaleString()}
            </span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              {roundTrip ? "ida y vuelta" : "solo ida"}
            </span>
          </div>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {firstDepart} → {lastArrival} ·{" "}
            {o.providerName === "mock" ? (
              <span className="font-medium text-amber-600 dark:text-amber-500">mock (no real)</span>
            ) : (
              o.providerName
            )}{" "}
            · {formatDuration(o.outbound.totalDurationMinutes)} · {stopsLabel(o.outbound.stops)}
            {o.validatingAirline && ` · ${o.validatingAirline}`}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {o.score !== undefined && (
            <span className="rounded-full bg-goblin-50 px-2 py-1 text-xs font-medium text-goblin-700 dark:bg-goblin-900/40 dark:text-goblin-500">
              score {o.score.toFixed(2)}
            </span>
          )}
          <span className="text-slate-400 dark:text-slate-500">{expanded ? "▾" : "▸"}</span>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-slate-200 px-4 pb-4 pt-3 text-sm dark:border-slate-800">
          <Leg label="Ida" itinerary={o.outbound} />
          {o.return && (
            <div className="mt-4">
              <Leg label="Vuelta" itinerary={o.return} />
            </div>
          )}
          {roundTrip && !o.return && (
            <div className="mt-4 rounded-md border border-dashed border-slate-300 p-3 text-xs text-slate-600 dark:border-slate-700 dark:text-slate-400">
              <strong>Vuelta:</strong> el precio incluye ida y vuelta pero SerpAPI requiere un
              segundo request por oferta para traer los detalles de la vuelta (horarios, aerolínea,
              escalas).
              {returnDate ? ` Fecha de vuelta: ${returnDate}.` : ""} Usá los deep links de abajo
              para ver el detalle completo.
            </div>
          )}

          <div className="mt-4 flex flex-wrap gap-2 border-t border-slate-200 pt-3 dark:border-slate-800">
            <a
              href={kayakUrl(firstDepart, lastArrival, departDate, returnDate)}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Ver en Kayak ↗
            </a>
            <a
              href={googleFlightsUrl(firstDepart, lastArrival, departDate, returnDate)}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Ver en Google Flights ↗
            </a>
            <a
              href={skyscannerUrl(firstDepart, lastArrival, departDate, returnDate)}
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Ver en Skyscanner ↗
            </a>
          </div>

          {o.providerName === "mock" && (
            <p className="mt-3 text-xs italic text-amber-600 dark:text-amber-500">
              Los detalles de esta oferta son sintéticos (mock). Activá "precios reales" en la
              búsqueda para consultar SerpAPI.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function Leg({ label, itinerary }: { label: string; itinerary: Itinerary }) {
  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          {label}
        </span>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          {formatDuration(itinerary.totalDurationMinutes)} · {stopsLabel(itinerary.stops)}
        </span>
      </div>
      <ol className="space-y-2">
        {itinerary.segments.map((s, i) => (
          <li key={`${s.from}-${s.to}-${i}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <div className="font-mono text-sm text-slate-900 dark:text-slate-100">
                  {s.from} {formatTime(s.departsAt)} → {s.to} {formatTime(s.arrivesAt)}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {s.airline} {s.flightNumber}
                  {s.aircraft ? ` · ${s.aircraft}` : ""} · {formatDuration(s.durationMinutes)}
                </div>
              </div>
            </div>
            {i < itinerary.segments.length - 1 && (
              <div className="ml-2 mt-1 border-l-2 border-dashed border-slate-300 pl-3 text-xs text-slate-500 dark:border-slate-700 dark:text-slate-400">
                escala en {s.to} · {formatLayover(s, itinerary.segments[i + 1])}
              </div>
            )}
          </li>
        ))}
      </ol>
    </div>
  );
}

function stopsLabel(stops: number): string {
  if (stops === 0) return "directo";
  return `${stops} escala${stops > 1 ? "s" : ""}`;
}

function formatDuration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function formatTime(iso: string): string {
  // Preserve whatever timezone offset came in; don't let the browser convert.
  const match = iso.match(/T(\d{2}):(\d{2})/);
  return match ? `${match[1]}:${match[2]}` : "";
}

function formatLayover(current: Segment, next: Segment | undefined): string {
  if (!next) return "";
  const start = new Date(current.arrivesAt).getTime();
  const end = new Date(next.departsAt).getTime();
  const mins = Math.max(0, Math.round((end - start) / 60000));
  return formatDuration(mins);
}

function kayakUrl(
  origin: string,
  destination: string,
  departDate: string,
  returnDate: string | undefined,
): string {
  const base = `https://www.kayak.com/flights/${origin}-${destination}/${departDate}`;
  return returnDate ? `${base}/${returnDate}?sort=price_a` : `${base}?sort=price_a`;
}

function googleFlightsUrl(
  origin: string,
  destination: string,
  departDate: string,
  returnDate: string | undefined,
): string {
  // Simple query-string form — Google parses this into a real search.
  const parts = [`flights from ${origin} to ${destination} on ${departDate}`];
  if (returnDate) parts.push(`returning ${returnDate}`);
  return `https://www.google.com/travel/flights?q=${encodeURIComponent(parts.join(" "))}`;
}

function skyscannerUrl(
  origin: string,
  destination: string,
  departDate: string,
  returnDate: string | undefined,
): string {
  // Skyscanner uses yymmdd (6 digits).
  const d = departDate.slice(2).replace(/-/g, "");
  const base = `https://www.skyscanner.com/transport/flights/${origin.toLowerCase()}/${destination.toLowerCase()}/${d}`;
  if (returnDate) {
    const r = returnDate.slice(2).replace(/-/g, "");
    return `${base}/${r}/`;
  }
  return `${base}/`;
}
